import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTTable from '../common/Table';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import RTFilters from '../common/RTFilters';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError, toastConfirm } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import tenantStorage from '../../utils/tenantStorage';
import '../common/rtCommon.css';
import './WhatsAppLogReport.css';
import RTPageLayout from '../common/RTPageLayout';

const isResendableLog = (log) =>
  Boolean(log) &&
  (log.status === 'failed' || log.delivery_status === 'failed') &&
  !log.do_not_send;

const WhatsAppLogReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [isResendingFailed, setIsResendingFailed] = useState(false);

  const getDefaultDates = () => {
    const today = new Date();
    const firstDayCurrentMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1,
    );
    return {
      from_date: firstDayCurrentMonth,
      to_date: today,
    };
  };

  const getDefaultFilters = () => ({
    from_date: getDefaultDates().from_date,
    to_date: getDefaultDates().to_date,
    template_name: '',
    status: '',
    search: '',
  });

  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);

  const resendableLogs = useMemo(() => logs.filter(isResendableLog), [logs]);
  const allResendableSelected =
    resendableLogs.length > 0 &&
    resendableLogs.every((log) => selectedLogIds.includes(log.id));

  const filterFields = useMemo(
    () => [
      {
        key: 'search',
        label: 'Name / Mobile',
        type: 'text',
        width: 'medium',
        placeholder: 'Search PG name or mobile',
        applyImmediately: false,
      },
      {
        key: 'from_date',
        label: 'From Date',
        type: 'date',
        width: 'compact',
      },
      {
        key: 'to_date',
        label: 'To Date',
        type: 'date',
        width: 'compact',
      },
      {
        key: 'template_name',
        label: 'Template Name',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: '', label: 'All Templates' },
          ...templates.map((template) => ({
            value: template,
            label: template,
          })),
        ],
      },
      {
        key: 'status',
        label: 'Status',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All Status' },
          { value: 'success', label: 'Success' },
          { value: 'failed', label: 'Failed' },
        ],
      },
    ],
    [templates],
  );

  const fetchLogs = async (
    filterParams = appliedFilters,
    abortController = null,
  ) => {
    setLoading(true);
    try {
      const params = {};
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.template_name)
        params.template_name = filterParams.template_name;
      if (filterParams.search && String(filterParams.search).trim()) {
        params.search = String(filterParams.search).trim();
      }

      // Format dates to YYYY-MM-DD in local timezone
      if (filterParams.from_date) {
        const year = filterParams.from_date.getFullYear();
        const month = String(filterParams.from_date.getMonth() + 1).padStart(
          2,
          '0',
        );
        const day = String(filterParams.from_date.getDate()).padStart(2, '0');
        params.from_date = `${year}-${month}-${day}`;
      }
      if (filterParams.to_date) {
        const year = filterParams.to_date.getFullYear();
        const month = String(filterParams.to_date.getMonth() + 1).padStart(
          2,
          '0',
        );
        const day = String(filterParams.to_date.getDate()).padStart(2, '0');
        params.to_date = `${year}-${month}-${day}`;
      }

      const token = tenantStorage.getItem('token');
      const response = await apiGet('/api/whatsapp/logs', {
        params: params,
        headers: { Authorization: `Bearer ${token}` },
        signal: abortController?.signal,
      });

      const apiResponse = response.data;
      if (apiResponse && Array.isArray(apiResponse)) {
        setLogs(apiResponse);
        setSelectedLogIds([]);
      } else {
        setLogs([]);
        setSelectedLogIds([]);
        toastError('No WhatsApp logs found for the selected filters.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading WhatsApp logs:', err);
        toastError('Failed to fetch WhatsApp logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = tenantStorage.getItem('token');
      const response = await apiGet('/api/whatsapp/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.templates) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading template names:', err);
      }
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchLogs(appliedFilters);

    // eslint-disable-next-line
  }, []);

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = {
      ...filters,
      [key]:
        value instanceof Date || value === null || value === undefined
          ? value
          : (value ?? ''),
    };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      fetchLogs(next);
    }
  };

  const handleFilterApply = () => {
    setAppliedFilters(filters);
    fetchLogs(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    fetchLogs(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (appliedFilters.from_date) {
      pills.push({
        key: 'from_date',
        label: `From: ${formatDateToDDMMYYYY(appliedFilters.from_date)}`,
      });
    }
    if (appliedFilters.to_date) {
      pills.push({
        key: 'to_date',
        label: `To: ${formatDateToDDMMYYYY(appliedFilters.to_date)}`,
      });
    }
    if (appliedFilters.template_name) {
      pills.push({
        key: 'template_name',
        label: `Template: ${appliedFilters.template_name}`,
      });
    }
    if (appliedFilters.status) {
      pills.push({
        key: 'status',
        label: `Status: ${appliedFilters.status.charAt(0).toUpperCase()}${appliedFilters.status.slice(1)}`,
      });
    }
    if (appliedFilters.search && String(appliedFilters.search).trim()) {
      pills.push({
        key: 'search',
        label: `Search: ${String(appliedFilters.search).trim()}`,
      });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const defaultFilters = getDefaultFilters();
    const next = { ...appliedFilters };
    if (key === 'from_date') {
      next.from_date = defaultFilters.from_date;
    } else if (key === 'to_date') {
      next.to_date = defaultFilters.to_date;
    } else {
      next[key] = '';
    }
    setFilters(next);
    setAppliedFilters(next);
    fetchLogs(next);
  };

  const handleSelectLog = (logId, checked) => {
    if (checked) {
      setSelectedLogIds((prev) =>
        prev.includes(logId) ? prev : [...prev, logId],
      );
    } else {
      setSelectedLogIds((prev) => prev.filter((id) => id !== logId));
    }
  };

  const handleSelectAllResendable = (checked) => {
    if (checked) {
      setSelectedLogIds(resendableLogs.map((log) => log.id));
    } else {
      setSelectedLogIds([]);
    }
  };

  const handleResend = async (log) => {
    try {
      setLoading(true);
      const token = tenantStorage.getItem('token');

      const response = await apiPost(
        `/api/whatsapp/resend/${log.id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        toastSuccess('Message resent successfully!');
        fetchLogs(appliedFilters);
      } else {
        toastError(apiResponse?.error || 'Failed to resend message');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error resending message:', err);
      toastError('Failed to resend message');
      setLoading(false);
    }
  };

  const handleToggleDoNotSend = async (log) => {
    try {
      const token = tenantStorage.getItem('token');

      const response = await apiPost(
        `/api/whatsapp/logs/${log.id}/mark-do-not-send`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        const action = apiResponse.do_not_send ? 'enabled' : 'disabled';
        toastSuccess(`Do not send flag ${action} successfully`);
        fetchLogs(appliedFilters);
      } else {
        toastError('Failed to toggle do not send flag');
      }
    } catch (err) {
      console.error('Error toggling do not send flag:', err);
      toastError('Failed to toggle do not send flag');
    }
  };

  const handleResendSelected = async () => {
    if (selectedLogIds.length === 0) {
      toastError('Select at least one failed message to resend');
      return;
    }

    const confirmed = await toastConfirm(
      true,
      `Resend ${selectedLogIds.length} selected failed WhatsApp message${selectedLogIds.length === 1 ? '' : 's'}?`,
    );
    if (!confirmed) {
      return;
    }

    setIsResendingFailed(true);
    try {
      const token = tenantStorage.getItem('token');

      const response = await apiPost(
        '/api/whatsapp/resend-failed',
        { log_ids: selectedLogIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 300000, // 5 minutes timeout for bulk resend
        },
      );

      const apiResponse = response.data;
      if (apiResponse && apiResponse.success) {
        const { succeeded, failed, total } = apiResponse;
        toastSuccess(
          `Resend completed: ${succeeded} succeeded, ${failed} failed out of ${total} attempts`,
        );
        setSelectedLogIds([]);
        fetchLogs(appliedFilters);
      } else {
        toastError('Failed to resend messages');
      }
    } catch (err) {
      console.error('Error resending failed messages:', err);
      toastError(err.response?.data?.detail || 'Failed to resend messages');
    } finally {
      setIsResendingFailed(false);
    }
  };

  const handleRefresh = () => {
    toastSuccess('Refreshing logs...');
    fetchLogs(appliedFilters);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getStatusBadgeClass = (status) => {
    const baseClass = 'whatsapp-log-status-badge';

    if (status === 'success') {
      return `${baseClass} whatsapp-log-status-success`;
    } else if (status === 'failed') {
      return `${baseClass} whatsapp-log-status-failed`;
    } else {
      return `${baseClass} whatsapp-log-status-unknown`;
    }
  };

  const getDeliveryStatusBadgeClass = (deliveryStatus) => {
    const baseClass = 'whatsapp-log-delivery-badge';

    switch (deliveryStatus) {
      case 'sent':
        return `${baseClass} whatsapp-log-delivery-sent`;
      case 'delivered':
        return `${baseClass} whatsapp-log-delivery-delivered`;
      case 'read':
        return `${baseClass} whatsapp-log-delivery-read`;
      case 'failed':
        return `${baseClass} whatsapp-log-delivery-failed`;
      default:
        return `${baseClass} whatsapp-log-delivery-pending`;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <input
            type="checkbox"
            checked={allResendableSelected}
            onChange={(e) => handleSelectAllResendable(e.target.checked)}
            disabled={
              resendableLogs.length === 0 || loading || isResendingFailed
            }
            title="Select all failed messages that can be resent"
            className="rounded border-gray-300"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        accessor: 'select',
        disableExport: true,
        width: 50,
        align: 'center',
        Cell: ({ row }) => {
          const canSelect = isResendableLog(row);
          return (
            <input
              type="checkbox"
              checked={canSelect && selectedLogIds.includes(row.id)}
              onChange={(e) => {
                if (!canSelect) return;
                handleSelectLog(row.id, e.target.checked);
              }}
              disabled={!canSelect || loading || isResendingFailed}
              title={
                canSelect
                  ? 'Select to resend'
                  : 'Only failed messages can be resent'
              }
              className="rounded border-gray-300"
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      },
      {
        Header: 'Sent At',
        accessor: 'created_at',
        Cell: ({ value }) => formatTimestamp(value),
      },
      { Header: 'Phone Number', accessor: 'phone_number' },
      { Header: 'Template', accessor: 'template_name' },
      {
        Header: 'Parameters',
        accessor: 'params',
        Cell: ({ value }) => {
          if (!value) return '-';
          try {
            const params =
              typeof value === 'string' ? JSON.parse(value) : value;
            return (
              <span className="whatsapp-log-params-text">
                {JSON.stringify(params)}
              </span>
            );
          } catch (e) {
            return value;
          }
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <span className={getStatusBadgeClass(value)}>
            {value || 'Unknown'}
          </span>
        ),
      },
      {
        Header: 'Delivery Status',
        accessor: 'delivery_status',
        Cell: ({ value }) => (
          <span className={getDeliveryStatusBadgeClass(value)}>
            {value || 'Pending'}
          </span>
        ),
      },
      {
        Header: 'Do Not Send',
        accessor: 'do_not_send',
        Cell: ({ value }) => (value ? 'Yes' : ''),
      },
      {
        Header: 'Error Message',
        accessor: 'error_message',
        Cell: ({ value }) => value || '-',
      },
      {
        Header: 'Actions',
        accessor: 'id',
        disableExport: true,
        Cell: ({ row }) => {
          const log = row;

          if (!log) return '-';

          const isFailed =
            log.status === 'failed' || log.delivery_status === 'failed';
          const showResend =
            isFailed &&
            log.template_name === 'payment_link' &&
            !log.do_not_send;
          const showDoNotSend = isFailed;

          if (!isFailed) return '-';

          return (
            <div className="d-flex gap-2">
              {showResend && (
                <RTButton
                  type="link"
                  size="sm"
                  onClick={() => handleResend(log)}
                  disabled={loading || isResendingFailed}
                >
                  Resend
                </RTButton>
              )}
              {showDoNotSend && (
                <RTButton
                  type="link"
                  size="sm"
                  onClick={() => handleToggleDoNotSend(log)}
                  disabled={loading || isResendingFailed}
                >
                  {log.do_not_send ? 'Unblock' : 'Block'}
                </RTButton>
              )}
            </div>
          );
        },
      },
    ],
    [
      allResendableSelected,
      resendableLogs.length,
      selectedLogIds,
      loading,
      isResendingFailed,
    ],
  );

  return (
    <RTPageLayout activePath={location.pathname} title="WhatsApp Message Logs">
      <div className="rt-section" style={{ flex: 'none' }}>
        <RTFilters
          fields={filterFields}
          values={filters}
          onChange={handleFilterChange}
          onApply={handleFilterApply}
          pills={appliedFilterPills}
          onRemovePill={handleRemoveAppliedFilter}
          onClearAll={handleClearFilters}
        />
      </div>

      {/* Table Section */}
      <div className="rt-section">
        {loading ? (
          <div className="whatsapp-log-spinner-container">
            <RTSpinner message="Loading WhatsApp logs..." />
          </div>
        ) : (
          <RTTable
            columns={columns}
            data={logs}
            noDataText="No WhatsApp logs found for the selected criteria"
          />
        )}
      </div>
    </RTPageLayout>
  );
};

export default WhatsAppLogReport;
